import { Link } from "@tanstack/react-router";
import {
  Check,
  CircleHelp,
  LogOut,
  Mail,
  PanelLeft,
  PanelTop,
  PlayCircle,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { DropdownMenu } from "radix-ui";

import { logout } from "@/api/session";
import { VersionFootnote } from "@/app/AppVersion";
import { useSession } from "@/app/session";
import { brand } from "@/brand";

import { useMenuLayout } from "./layoutPref";

export function UserMenu() {
  const { user } = useSession();
  const [layout, setLayout] = useMenuLayout();
  if (!user) return null;
  const initial = (user.display_name || user.email).slice(0, 1).toUpperCase();

  // The shell sees the signed-out state and goes to the sign-in page.
  function signOut(everywhere: boolean) {
    void logout(everywhere);
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label="Account"
        className="grid size-9 place-items-center rounded-full bg-gradient-to-br from-[#3b74ff] to-[#7b5cff] text-sm font-bold text-white hover:ring-2 hover:ring-brand/30"
      >
        {initial}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-56 rounded-lg border border-border bg-surface p-1 shadow-card"
        >
          <div className="px-2.5 py-2">
            <div className="truncate text-sm font-medium text-fg">
              {user.display_name || user.email}
            </div>
            <div className="flex items-center gap-1 text-xs text-muted">
              {user.role === "owner" ? (
                <ShieldCheck className="size-3.5 text-accent" />
              ) : null}
              <span className="truncate">{user.email}</span>
            </div>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          {/* every personal setting lives on Account (owner, 1 Nov 2026) */}
          <DropdownMenu.Item asChild className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm text-fg outline-none data-[highlighted]:bg-surface-2">
            <Link to="/app/account">
              <UserRound className="size-4 text-subtle" /> Account and settings
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Label className="px-2.5 pt-1 pb-0.5 text-2xs font-semibold tracking-wide text-subtle uppercase">
            Menu layout
          </DropdownMenu.Label>
          {(
            [
              ["side", "Side menu", PanelLeft],
              ["top", "Top menu", PanelTop],
            ] as const
          ).map(([value, label, Icon]) => (
            <DropdownMenu.Item
              key={value}
              onSelect={() => setLayout(value)}
              className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm text-fg outline-none data-[highlighted]:bg-surface-2"
            >
              <Icon className="size-4 text-subtle" /> {label}
              {layout === value ? (
                <Check
                  className="ml-auto size-4 text-brand"
                  aria-label="current"
                />
              ) : null}
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Label className="px-2.5 pt-1 pb-0.5 text-2xs font-semibold tracking-wide text-subtle uppercase">
            Help
          </DropdownMenu.Label>
          {/* public pages (videos, FAQ) open in a new tab, so the app stays where it was */}
          <DropdownMenu.Item asChild className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm text-fg outline-none data-[highlighted]:bg-surface-2">
            <a href="/how-it-works" target="_blank" rel="noopener">
              <PlayCircle className="size-4 text-subtle" /> How it works (videos)
            </a>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm text-fg outline-none data-[highlighted]:bg-surface-2">
            <a href="/faq" target="_blank" rel="noopener">
              <CircleHelp className="size-4 text-subtle" /> FAQ
            </a>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm text-fg outline-none data-[highlighted]:bg-surface-2">
            <a href={`mailto:${brand.supportEmail}`}>
              <Mail className="size-4 text-subtle" />
              <span className="truncate">Contact {brand.supportEmail}</span>
            </a>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            onSelect={() => signOut(false)}
            className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm text-fg outline-none data-[highlighted]:bg-surface-2"
          >
            <LogOut className="size-4 text-subtle" /> Sign out
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => signOut(true)}
            className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm text-fg outline-none data-[highlighted]:bg-surface-2"
          >
            <LogOut className="size-4 text-subtle" /> Sign out on all devices
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          {/* the version a member reads out to support (Account -> About this app) */}
          <div className="px-2.5 pt-0.5 pb-1">
            <VersionFootnote />
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
